/**
 * @doit/shared — საერთო ტიპები, enum-ები და ქართული ლეიბლები.
 * იყენებს როგორც API, ისე Web. ერთი წყარო — არ დუბლირდება.
 */

// ============================================================
// ROLES
// ============================================================

export const ROLES = ['ADMIN', 'MANAGER', 'LECTURER'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<Role, string> = {
  ADMIN: 'ადმინისტრატორი',
  MANAGER: 'მენეჯერი',
  LECTURER: 'ლექტორი',
};

export const USER_STATUSES = ['INVITED', 'ACTIVE', 'DISABLED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  INVITED: 'მოწვეული',
  ACTIVE: 'აქტიური',
  DISABLED: 'გათიშული',
};

// ============================================================
// ჯგუფი
// ============================================================

/** საგნის ეტაპი — რომაული ნომერი UI-სთვის */
export function stageLabel(stage: number): string {
  const roman = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
  return `${roman[stage] ?? stage} ეტაპი`;
}

/** ლექციის დასაშვები ხანგრძლივობა საათებში */
export const LESSON_HOURS_OPTIONS = [1, 2, 3, 4] as const;
export type LessonHours = (typeof LESSON_HOURS_OPTIONS)[number];

/** საათები ეტაპზე ÷ ლექციის ხანგრძლივობა = ლექციების რაოდენობა */
export function lessonsPerStage(hoursPerStage: number, lessonHours: number): number {
  if (!hoursPerStage || !lessonHours) return 0;
  return Math.ceil(hoursPerStage / lessonHours);
}

export const GROUP_FORMATS = ['ONLINE', 'OFFLINE', 'HYBRID'] as const;
export type GroupFormat = (typeof GROUP_FORMATS)[number];

export const GROUP_FORMAT_LABEL: Record<GroupFormat, string> = {
  ONLINE: 'ონლაინ',
  OFFLINE: 'ოფლაინ',
  HYBRID: 'ჰიბრიდი',
};

export const GROUP_STATUSES = ['PLANNED', 'ACTIVE', 'PAUSED', 'FINISHED', 'CANCELLED'] as const;
export type GroupStatus = (typeof GROUP_STATUSES)[number];

export const GROUP_STATUS_LABEL: Record<GroupStatus, string> = {
  PLANNED: 'დაგეგმილი',
  ACTIVE: 'მიმდინარე',
  PAUSED: 'შეჩერებული',
  FINISHED: 'დასრულებული',
  CANCELLED: 'გაუქმებული',
};

export const LECTURER_ROLES = ['PRIMARY', 'ASSISTANT'] as const;
export type LecturerRole = (typeof LECTURER_ROLES)[number];

export const LECTURER_ROLE_LABEL: Record<LecturerRole, string> = {
  PRIMARY: 'ძირითადი',
  ASSISTANT: 'ასისტენტი',
};

// ============================================================
// სტუდენტი / ჩარიცხვა
// ============================================================

export const STUDENT_STATUSES = ['ACTIVE', 'PAUSED', 'DROPPED', 'GRADUATED'] as const;
export type StudentStatus = (typeof STUDENT_STATUSES)[number];

export const STUDENT_STATUS_LABEL: Record<StudentStatus, string> = {
  ACTIVE: 'აქტიური',
  PAUSED: 'შეჩერებული',
  DROPPED: 'შეწყვეტილი',
  GRADUATED: 'კურსდამთავრებული',
};

export const ENROLLMENT_STATUSES = ['ACTIVE', 'TRANSFERRED', 'DROPPED', 'COMPLETED'] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

export const ENROLLMENT_STATUS_LABEL: Record<EnrollmentStatus, string> = {
  ACTIVE: 'აქტიური',
  TRANSFERRED: 'გადაყვანილი',
  DROPPED: 'შეწყვეტილი',
  COMPLETED: 'დასრულებული',
};

// ============================================================
// გაკვეთილი / დასწრება
// ============================================================

export const SESSION_STATUSES = ['SCHEDULED', 'HELD', 'CANCELLED', 'POSTPONED'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const SESSION_STATUS_LABEL: Record<SessionStatus, string> = {
  SCHEDULED: 'დაგეგმილი',
  HELD: 'ჩატარებული',
  CANCELLED: 'გაუქმებული',
  POSTPONED: 'გადადებული',
};

export const ATTENDANCE_STATUSES = ['PRESENT', 'ABSENT'] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

export const ATTENDANCE_STATUS_LABEL: Record<AttendanceStatus, string> = {
  PRESENT: 'ესწრება',
  ABSENT: 'აცდენს',
};

export const ATTENDANCE_STATUS_SHORT: Record<AttendanceStatus, string> = {
  PRESENT: 'ე',
  ABSENT: 'ა',
};

// ============================================================
// შეფასება / ფლაგები
// ============================================================

export const STAGE_RESULT_TYPES = ['PROJECT', 'TASK', 'SCORE'] as const;
export type StageResultType = (typeof STAGE_RESULT_TYPES)[number];

export const STAGE_RESULT_TYPE_LABEL: Record<StageResultType, string> = {
  PROJECT: 'პროექტი',
  TASK: 'დავალება',
  SCORE: 'ქულა',
};

// ============================================================
// სერტიფიკატი
// ============================================================

/**
 * ამ დასწრებიდან (ჩათვლით) დავალება + პროექტი = „სერტ + საერთაშორისო".
 * დასწრება — მიმდინარე სტატუსით (`certAttendancePct`); ეტაპის ბოლოს საბოლოოა.
 */
export const INTL_CERT_MIN_PCT = 51;

export const CERT_KINDS = ['INTERNATIONAL', 'CERT', 'NONE'] as const;
export type CertKind = (typeof CERT_KINDS)[number];

/** მოკლე ტექსტი — სიებშიც ეტევა; მენეჯერის ხელით არჩევისას `certStatus`-ში ინახება */
export const CERT_LABEL: Record<CertKind, string> = {
  INTERNATIONAL: 'სერტ + საერთაშორისო',
  CERT: 'სერტ',
  NONE: 'არა',
};

/**
 * უკვე ჩაწერილი ტექსტები → სტატუსი. ბაზაში არაფერი იცვლება — მხოლოდ ჩვენება:
 * CSV-იმპორტის „სერთიფიკატი" და წინა ვერსიის გრძელი წარწერები მოკლედ ჩანს.
 */
const LEGACY_CERT_KIND: Record<string, CertKind> = {
  სერთიფიკატი: 'CERT',
  სერტიფიკატი: 'CERT',
  'სერტიფიკატი · დავალება': 'CERT',
  'სერტიფიკატი · პროექტი': 'CERT',
  'სერტიფიკატი · დავალება + პროექტი': 'CERT',
  'საერთაშორისო სერტიფიცირება': 'INTERNATIONAL',
  'სერტიფიკატის გარეშე': 'NONE',
};

/** ტექსტი → სტატუსი (ახალი, ძველი თუ იმპორტირებული); უცნობი → `null` */
export function certKindOf(text: string | null | undefined): CertKind | null {
  const t = text?.trim();
  if (!t) return null;
  return CERT_KINDS.find((k) => CERT_LABEL[k] === t) ?? LEGACY_CERT_KIND[t] ?? null;
}

/**
 * სერტიფიკატის დასწრება — **მიმდინარე სტატუსით**: დასწრებული / უკვე ჩატარებული
 * (მონიშნული) ლექციები, იგივე ფორმულა, რაც ჟურნალში.
 *
 * ეტაპის განმავლობაში ის დღევანდელ მდგომარეობას აჩვენებს; როცა ჯგუფზე გაწერილი
 * ყველა ლექცია ჩატარდება, მონიშნული = ჯამი და ეს რიცხვი საბოლოო შედეგი ხდება.
 */
export function certAttendancePct(input: { present: number; marked: number }): number | null {
  if (input.marked <= 0) return null;
  return Math.round((input.present / input.marked) * 1000) / 10;
}

/**
 * ეტაპის სერტიფიკატი შესრულებულიდან და დასწრებიდან:
 *   შეწყვეტილი                                   → არა
 *   ორივე (დავალება + პროექტი) და დასწრება ≥ 51%  → სერტ + საერთაშორისო
 *   დავალება ან პროექტი, ან დასწრება ≥ 51%         → სერტ
 *   დასწრება < 51% და არცერთი                     → არა
 * დასწრება უცნობი = 51%-ს ვერ ადასტურებს.
 */
export function deriveCertificate(input: {
  task: boolean;
  project: boolean;
  pct: number | null | undefined;
  /** შეწყვეტილი სტატისტიკაში ითვლება, სერტიფიკატზე კი „არა" ეწერება */
  dropped?: boolean;
}): CertKind {
  if (input.dropped) return 'NONE';
  const pct = input.pct;
  const attended = pct !== null && pct !== undefined && pct >= INTL_CERT_MIN_PCT;
  if (input.task && input.project && attended) return 'INTERNATIONAL';
  if (input.task || input.project || attended) return 'CERT';
  return 'NONE';
}

/** რა არის შესრულებული — სიის სვეტისთვის */
export function doneLabel(input: { task: boolean; project: boolean }): string {
  if (input.task && input.project) return 'ორივე';
  if (input.task) return 'დავალება';
  if (input.project) return 'პროექტი';
  return '—';
}

/**
 * რაც სერტიფიკატზე რეალურად ეწერება.
 *
 * ეტაპი ჯერ არ შეფასებულა (შედეგი არ არის) → `null`: სტატუსი არ ენიჭება, მარტო
 * დასწრებიდან არაფერი იწერება. შეწყვეტილს ყოველთვის „არა" ეწერება.
 *
 * ხელით ჩაწერილი ტექსტი (მენეჯერის არჩევანი ან CSV-იმპორტი) ყოველთვის იმარჯვებს —
 * შეწყვეტილზეც: მენეჯერს ყველაფრის ხელით შეცვლა შეუძლია. ცნობილი ტექსტი მოკლე
 * წარწერით ჩანს, უცნობი — როგორც წერია.
 */
export function effectiveCertificate(
  rows: readonly { resultType: string; certStatus: string | null }[],
  pct: number | null | undefined,
  options: { dropped?: boolean } = {},
): { text: string; kind: CertKind | null; manual: boolean } | null {
  const manual = rows.find((r) => r.certStatus && r.certStatus.trim())?.certStatus?.trim();
  if (manual) {
    const kind = certKindOf(manual);
    return { text: kind ? CERT_LABEL[kind] : manual, kind, manual: true };
  }
  if (options.dropped) return { text: CERT_LABEL.NONE, kind: 'NONE', manual: false };
  if (rows.length === 0) return null;
  const kind = deriveCertificate({
    task: rows.some((r) => r.resultType === 'TASK'),
    project: rows.some((r) => r.resultType === 'PROJECT'),
    pct,
  });
  return { text: CERT_LABEL[kind], kind, manual: false };
}

export const FLAG_TYPES = ['CANNOT_REACH', 'EMAIL_SENT', 'MONITORING', 'DROPPED_OUT'] as const;
export type FlagType = (typeof FLAG_TYPES)[number];

export const FLAG_LABEL: Record<FlagType, string> = {
  CANNOT_REACH: 'ვერ დავუკავშირდი',
  EMAIL_SENT: 'მეილი მივწერე',
  MONITORING: 'მონიტორინგზე',
  DROPPED_OUT: 'შეწყვიტა',
};

export const FLAG_ICON: Record<FlagType, string> = {
  CANNOT_REACH: '📵',
  EMAIL_SENT: '✉️',
  MONITORING: '👁',
  DROPPED_OUT: '⚫',
};

/** Kanban სვეტების თანმიმდევრობა */
export const BOARD_COLUMNS = ['RISK', ...FLAG_TYPES] as const;
export type BoardColumn = (typeof BOARD_COLUMNS)[number];

export const BOARD_COLUMN_LABEL: Record<BoardColumn, string> = {
  RISK: 'დასამუშავებელი',
  ...FLAG_LABEL,
};

// ============================================================
// კვირის დღეები (ISO: 1 = ორშაბათი … 7 = კვირა)
// ============================================================

export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABEL: Record<Weekday, string> = {
  1: 'ორშაბათი',
  2: 'სამშაბათი',
  3: 'ოთხშაბათი',
  4: 'ხუთშაბათი',
  5: 'პარასკევი',
  6: 'შაბათი',
  7: 'კვირა',
};

export const WEEKDAY_SHORT: Record<Weekday, string> = {
  1: 'ორშ',
  2: 'სამ',
  3: 'ოთხ',
  4: 'ხუთ',
  5: 'პარ',
  6: 'შაბ',
  7: 'კვი',
};

/** „ორშაბათი-ხუთშაბათი" → [1, 4] */
export function parseWeekdayPattern(pattern: string): number[] {
  const byName = new Map<string, number>(
    (Object.entries(WEEKDAY_LABEL) as [string, string][]).map(([n, label]) => [label, Number(n)]),
  );
  return pattern
    .split('-')
    .map((part) => byName.get(part.trim()))
    .filter((n): n is number => typeof n === 'number')
    .sort((a, b) => a - b);
}

/** [1, 4] → „ორშაბათი-ხუთშაბათი" */
export function formatWeekdays(days: number[], short = false): string {
  const dict = short ? WEEKDAY_SHORT : WEEKDAY_LABEL;
  return [...days]
    .sort((a, b) => a - b)
    .map((d) => dict[d as Weekday] ?? String(d))
    .join('-');
}

// ============================================================
// რისკის ზღვრები (ნაგულისხმევი — settings-ით იცვლება)
// ============================================================

export const DEFAULT_SETTINGS = {
  /** ამ ზღვარს ქვემოთ სტუდენტი რისკშია */
  risk_threshold: 75,
  /** ყვითელი ზონის ზედა ზღვარი */
  yellow_threshold: 65,
  /** წითელი ზონის ზედა ზღვარი */
  red_threshold: 51,
  /** ამდენი ზედიზედ გაცდენა = რისკი, პროცენტის მიუხედავად */
  streak_risk_min: 3,
  /** ნაგულისხმევი პროექტის ფილტრი: პროექტის id ან 'all' */
  active_project: 'all',
} as const;

export type AppSettings = {
  risk_threshold: number;
  yellow_threshold: number;
  red_threshold: number;
  streak_risk_min: number;
  /** პროექტის id ან 'all' */
  active_project: string;
};


/** დასწრების ფერითი ზონა */
export function attendanceZone(
  pct: number | null | undefined,
  s: Pick<AppSettings, 'risk_threshold' | 'yellow_threshold' | 'red_threshold'>,
): 'green' | 'yellow' | 'red' | 'grey' {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return 'grey';
  if (pct < s.red_threshold) return 'red';
  if (pct < s.yellow_threshold) return 'yellow';
  if (pct < s.risk_threshold) return 'yellow';
  return 'green';
}

// ============================================================
// ჰელპერები
// ============================================================

/** ტელეფონი მხოლოდ ციფრებით — join-ებისა და დედუპლიკაციისთვის */
export function normalizePhone(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).replace(/\D/g, '');
}

/**
 * დასწრების უსაფრთხო პარსინგი (legacy CSV-სთვის).
 * აბრუნებს 0–100 პროცენტს ან null-ს, თუ მონაცემი არ არის/გაუმართავია.
 * ცარიელი, "#REF!", "#N/A" → null (და არა 0!).
 */
export function parseLegacyAttendance(raw: unknown): number | null {
  if (raw === '' || raw === null || raw === undefined) return null;
  let n: number;
  if (typeof raw === 'number') {
    n = raw;
  } else {
    let s = String(raw).trim();
    if (!s || s.startsWith('#')) return null;
    const hadPercent = s.includes('%');
    s = s.replace('%', '').replace(',', '.').trim();
    n = parseFloat(s);
    if (Number.isNaN(n)) return null;
    if (hadPercent) return Math.round(n * 10) / 10;
  }
  if (n < 0) return null;
  if (n <= 1) return Math.round(n * 1000) / 10;
  return Math.round(n * 10) / 10;
}

export const TIMEZONE = 'Asia/Tbilisi';
